import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import axios from 'axios';
import { CheckCircle, Loader2 } from 'lucide-react';
import { AuthContext } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const { checkAuth } = useContext(AuthContext);
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 5;

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(
        `${API}/payments/checkout/status/${sessionId}`,
        { withCredentials: true }
      );

      if (response.data.payment_status === 'paid') {
        setStatus('success');
        await checkAuth();
      } else if (response.data.status === 'expired') {
        setStatus('expired');
      } else {
        // Continue polling
        setAttempts(prev => prev + 1);
        setTimeout(pollPaymentStatus, 2000);
      }
    } catch (error) {
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
      <Card className="glass border-white/20 max-w-md w-full mx-4">
        <CardHeader>
          <CardTitle className="text-2xl text-white text-center" style={{ fontFamily: 'Space Grotesk' }}>
            Payment Status
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-6">
          {status === 'checking' && (
            <>
              <Loader2 className="w-16 h-16 mx-auto text-white animate-spin" />
              <p className="text-white">Verifying your payment...</p>
            </>
          )}
          {status === 'success' && (
            <>
              <CheckCircle className="w-16 h-16 mx-auto text-green-400" />
              <div className="space-y-2">
                <p className="text-white text-lg font-semibold">Payment Successful!</p>
                <p className="text-white/80">Your subscription is now active.</p>
              </div>
              <Button
                data-testid="continue-button"
                onClick={() => navigate('/dashboard')}
                className="bg-white text-purple-700 hover:bg-gray-100 w-full"
              >
                Continue to Dashboard
              </Button>
            </>
          )}
          {status === 'expired' && (
            <>
              <p className="text-white">Payment session expired. Please try again.</p>
              <Button
                onClick={() => navigate('/dashboard')}
                className="bg-white text-purple-700 hover:bg-gray-100 w-full"
              >
                Back to Dashboard
              </Button>
            </>
          )}
          {status === 'timeout' && (
            <>
              <p className="text-white">Payment verification timed out. Please check your email for confirmation.</p>
              <Button
                onClick={() => navigate('/dashboard')}
                className="bg-white text-purple-700 hover:bg-gray-100 w-full"
              >
                Back to Dashboard
              </Button>
            </>
          )}
          {status === 'error' && (
            <>
              <p className="text-white">An error occurred. Please contact support.</p>
              <Button
                onClick={() => navigate('/dashboard')}
                className="bg-white text-purple-700 hover:bg-gray-100 w-full"
              >
                Back to Dashboard
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccess;
